import { LoadingBlock } from "../../UI/Loading/LoadingRows.tsx";
import { useMemo } from "react";
import dayjs from "dayjs";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "../../UI/Card.tsx";
import { useTeacherStudentReviewHistory } from "./useTeacherStudentReviewHistory.ts";
import { TeacherCoordinatorReviewHistoryItem } from "./useTeacherCoordinatorReviewHistory.ts";
import {
  buildTeacherScoreTrend,
  CoordinatorReviewMark,
  TeacherScoreTrendPoint,
} from "./buildTeacherScoreTrend.ts";
import {
  compareScoreWindowWithPrevious,
  ScoreWindowComparison,
} from "./studentReviewWindows.ts";
import {
  coordinatorWeightedScore,
  STUDENT_REVIEW_DECAY_HALF_LIFE_DAYS,
  STUDENT_SCORE_WINDOW_MONTHS,
} from "./teacherScoreAlgorithm.ts";
import {
  COORDINATOR_COLOR,
  COORDINATOR_REVIEW_PERIOD_COLOR,
  STUDENT_COLOR,
  STUDENT_COLOR_OUTSIDE_SCORE_WINDOW,
} from "./teacherScoreColors.ts";
import { ScoreDelta } from "./ScoreDelta.tsx";
import {
  AXIS_COLOR,
  AXIS_TICK,
  GRID_COLOR,
  SECONDARY_INK,
  SURFACE,
  yAxisTitle,
} from "../../UI/Chart/chartStyle.ts";
import { ChartLegendItem } from "../../UI/Chart/ChartLegendItem.tsx";
import { SummaryStat } from "../../UI/Chart/SummaryStat.tsx";

const SCORE_WINDOW_WASH = "#f0efec";

const CHART_MARGIN = { top: 24, right: 8, left: 0, bottom: 0 };
const Y_AXIS_WIDTH = 48;
const SYNC_ID = "teacher-score-trend";

const scoreWindowLabel = `last ${STUDENT_SCORE_WINDOW_MONTHS} months`;

function periodAxisEnd(points: TeacherScoreTrendPoint[]): number {
  return points.length - 0.5;
}

function periodAxisProps(points: TeacherScoreTrendPoint[]) {
  return {
    dataKey: "periodIndex",
    type: "number" as const,
    domain: [-0.5, periodAxisEnd(points)],
    ticks: points.map((_, periodIndex) => periodIndex),
    tickFormatter: (periodIndex: number) => points[periodIndex]?.label ?? "",
  };
}

function scoreWindowAreaProps(points: TeacherScoreTrendPoint[]) {
  return {
    x1: points.findIndex((point) => point.isInScoreWindow) - 0.5,
    x2: periodAxisEnd(points),
    fill: SCORE_WINDOW_WASH,
    fillOpacity: 1,
  };
}

type ChartCoordinate = { x: number; y: number };
type ChartSegment = [ChartCoordinate, ChartCoordinate];

function coordinatorScoreSegments(
  marks: CoordinatorReviewMark[],
  axisEnd: number,
): ChartSegment[] {
  return marks.flatMap((mark, index): ChartSegment[] => {
    const nextMark = marks[index + 1];
    const heldUntil = nextMark ? nextMark.periodAxisPosition : axisEnd;
    const heldScore: ChartSegment = [
      { x: mark.periodAxisPosition, y: mark.score },
      { x: heldUntil, y: mark.score },
    ];
    if (!nextMark) return [heldScore];

    const stepToNextScore: ChartSegment = [
      { x: heldUntil, y: mark.score },
      { x: heldUntil, y: nextMark.score },
    ];
    return [heldScore, stepToNextScore];
  });
}

function formatPeriodRange(point: TeacherScoreTrendPoint): string {
  return `${point.firstDay.format("D MMM")} – ${point.lastDay.format("D MMM YYYY")}`;
}

function formatScore(score: number | null): string {
  return score === null ? "—" : score.toFixed(2);
}

function ScoreTrendTooltip({ active, payload }: TooltipProps<number, string>) {
  const point = payload?.[0]?.payload as TeacherScoreTrendPoint | undefined;
  if (!active || !point) return null;

  const reviewInForce = point.coordinatorReviewInForce;

  return (
    <div
      className={
        "bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-xs flex flex-col gap-1.5 min-w-56"
      }
    >
      <div className={"flex items-center justify-between gap-3"}>
        <span className={"font-semibold text-slate-700"}>
          {formatPeriodRange(point)}
        </span>
        {point.isInScoreWindow && (
          <span className={"text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded"}>
            Used by the score
          </span>
        )}
      </div>
      <div className={"flex items-center justify-between gap-3"}>
        <span className={"flex items-center gap-1.5 text-slate-500"}>
          <span
            className={"w-2.5 h-2.5 rounded-full"}
            style={{ backgroundColor: STUDENT_COLOR }}
          />
          Student reviews avg
        </span>
        <span className={"font-semibold text-slate-800"}>
          {formatScore(point.studentAverage)}
        </span>
      </div>
      <div className={"flex items-center justify-between gap-3 pl-4"}>
        <span className={"text-slate-400"}>Reviews · students</span>
        <span className={"text-slate-600"}>
          {point.studentReviewCount} · {point.reviewingStudentCount}
        </span>
      </div>
      <div className={"flex items-center justify-between gap-3"}>
        <span className={"flex items-center gap-1.5 text-slate-500"}>
          <span
            className={"w-2.5 h-2.5 rounded-full"}
            style={{ backgroundColor: COORDINATOR_COLOR }}
          />
          Coordinator score
        </span>
        <span className={"font-semibold text-slate-800"}>
          {formatScore(
            reviewInForce
              ? coordinatorWeightedScore(reviewInForce.scores)
              : null,
          )}
        </span>
      </div>
      {reviewInForce && (
        <div className={"pl-4 text-slate-400"}>
          {point.coordinatorReviewSubmittedInPeriod ? "New review" : "Review"}{" "}
          of {dayjs(reviewInForce.reviewedAtUtc).format("D MMM YYYY")} · Fluency{" "}
          {reviewInForce.scores.fluencyScore} · Presence{" "}
          {reviewInForce.scores.presenceScore} · Clarity{" "}
          {reviewInForce.scores.clarityScore}
        </div>
      )}
    </div>
  );
}

function StudentAverageDelta({
  scoreWindow,
  previousWindow,
}: ScoreWindowComparison) {
  if (scoreWindow.average === null) {
    return (
      <span className={"text-xs text-slate-400"}>
        No student reviews in the {scoreWindowLabel}
      </span>
    );
  }

  if (previousWindow.average === null) {
    return (
      <span className={"text-xs text-slate-400"}>
        No reviews in the previous {STUDENT_SCORE_WINDOW_MONTHS} months to
        compare
      </span>
    );
  }

  return (
    <span className={"text-xs"}>
      <ScoreDelta
        current={scoreWindow.average}
        previous={previousWindow.average}
      />{" "}
      <span className={"text-slate-400"}>
        vs previous {STUDENT_SCORE_WINDOW_MONTHS} months (
        {previousWindow.average.toFixed(2)})
      </span>
    </span>
  );
}

export function TeacherScoreTrendCard({
  teacherId,
  coordinatorReviews,
}: {
  teacherId: string;
  coordinatorReviews: TeacherCoordinatorReviewHistoryItem[];
}) {
  const { studentReviewGroups, loading } =
    useTeacherStudentReviewHistory(teacherId);
  const now = useMemo(() => dayjs(), []);

  const { points, coordinatorReviewMarks, coordinatorReviewPeriodStarts } =
    useMemo(
      () =>
        buildTeacherScoreTrend({
          studentReviewGroups,
          coordinatorReviews,
          now,
        }),
      [studentReviewGroups, coordinatorReviews, now],
    );

  const windowComparison = useMemo(
    () => compareScoreWindowWithPrevious({ studentReviewGroups, now }),
    [studentReviewGroups, now],
  );

  const chartPoints = useMemo(
    () => points.map((point, periodIndex) => ({ ...point, periodIndex })),
    [points],
  );

  const coordinatorSegments = useMemo(
    () =>
      coordinatorScoreSegments(coordinatorReviewMarks, periodAxisEnd(points)),
    [coordinatorReviewMarks, points],
  );

  const latestCoordinatorReview = coordinatorReviews[0] ?? null;

  return (
    <Card>
      <h2
        className={
          "text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4"
        }
      >
        Score Trend
      </h2>

      {loading ? (
        <LoadingBlock sizeClassName={"h-96 w-full"} />
      ) : points.length === 0 ? (
        <p className={"text-sm text-slate-400"}>No reviews yet.</p>
      ) : (
        <div className={"flex flex-col gap-4"}>
          <div className={"flex flex-wrap gap-x-10 gap-y-4"}>
            <SummaryStat
              label={`Student reviews · plain avg · ${scoreWindowLabel}`}
              value={formatScore(windowComparison.scoreWindow.average)}
            >
              <StudentAverageDelta {...windowComparison} />
            </SummaryStat>
            <SummaryStat
              label={`Reviews · students · ${scoreWindowLabel}`}
              value={`${windowComparison.scoreWindow.reviewCount} · ${windowComparison.scoreWindow.studentCount}`}
            />
            <SummaryStat
              label={"Latest coordinator review"}
              value={
                latestCoordinatorReview
                  ? formatScore(
                      coordinatorWeightedScore(latestCoordinatorReview.scores),
                    )
                  : "—"
              }
            >
              {latestCoordinatorReview && (
                <span className={"text-xs text-slate-400"}>
                  {dayjs(latestCoordinatorReview.reviewedAtUtc).format(
                    "D MMM YYYY",
                  )}
                </span>
              )}
            </SummaryStat>
          </div>

          <div className={"flex flex-wrap items-center gap-x-5 gap-y-2"}>
            <ChartLegendItem
              color={STUDENT_COLOR}
              label={"Student reviews · monthly average"}
              shape={"line"}
            />
            <ChartLegendItem
              color={COORDINATOR_COLOR}
              label={"Coordinator score · held until the next review"}
              shape={"line"}
            />
            <ChartLegendItem
              color={COORDINATOR_REVIEW_PERIOD_COLOR}
              label={"New coordinator review period"}
              shape={"divider"}
            />
            <ChartLegendItem
              color={STUDENT_COLOR}
              label={"Student reviews per month"}
              shape={"block"}
            />
            <ChartLegendItem
              color={SCORE_WINDOW_WASH}
              label={`Student reviews used by the score · ${scoreWindowLabel}`}
              shape={"block"}
            />
          </div>

          <div>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart
                data={chartPoints}
                syncId={SYNC_ID}
                margin={CHART_MARGIN}
              >
                <ReferenceArea
                  {...scoreWindowAreaProps(points)}
                  label={{
                    value: "Student reviews used by the score",
                    position: "top",
                    fontSize: 11,
                    fill: SECONDARY_INK,
                  }}
                />
                <CartesianGrid vertical={false} stroke={GRID_COLOR} />
                <XAxis {...periodAxisProps(points)} hide />
                <YAxis
                  domain={[1, 5]}
                  ticks={[1, 2, 3, 4, 5]}
                  label={yAxisTitle("Score")}
                  width={Y_AXIS_WIDTH}
                  tick={AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  content={<ScoreTrendTooltip />}
                  filterNull={false}
                  cursor={{ stroke: AXIS_COLOR }}
                />
                {coordinatorReviewPeriodStarts.map(
                  ({ date, periodAxisPosition }) => (
                    <ReferenceLine
                      key={date.valueOf()}
                      x={periodAxisPosition}
                      stroke={COORDINATOR_REVIEW_PERIOD_COLOR}
                    />
                  ),
                )}
                {coordinatorSegments.map((segment, index) => (
                  <ReferenceLine
                    key={index}
                    segment={segment}
                    stroke={COORDINATOR_COLOR}
                    strokeWidth={2}
                  />
                ))}
                <Line
                  dataKey="studentAverage"
                  name="Student reviews avg"
                  stroke={STUDENT_COLOR}
                  strokeWidth={2}
                  dot={{
                    r: 4,
                    fill: STUDENT_COLOR,
                    stroke: SURFACE,
                    strokeWidth: 2,
                  }}
                  activeDot={{ r: 5, stroke: SURFACE, strokeWidth: 2 }}
                  connectNulls
                  isAnimationActive={false}
                />
                {coordinatorReviewMarks.map(
                  ({ review, periodAxisPosition, score }) => (
                    <ReferenceDot
                      key={review.id}
                      x={periodAxisPosition}
                      y={score}
                      r={5}
                      fill={COORDINATOR_COLOR}
                      stroke={SURFACE}
                      strokeWidth={2}
                    />
                  ),
                )}
              </ComposedChart>
            </ResponsiveContainer>
            <ResponsiveContainer width="100%" height={120}>
              <ComposedChart
                data={chartPoints}
                syncId={SYNC_ID}
                margin={{ ...CHART_MARGIN, top: 12 }}
              >
                <ReferenceArea {...scoreWindowAreaProps(points)} />
                <CartesianGrid vertical={false} stroke={GRID_COLOR} />
                <XAxis
                  {...periodAxisProps(points)}
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={{ stroke: AXIS_COLOR }}
                />
                <YAxis
                  allowDecimals={false}
                  label={yAxisTitle("Reviews")}
                  width={Y_AXIS_WIDTH}
                  tick={AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={() => null} cursor={false} />
                <Bar
                  dataKey="studentReviewCount"
                  name="Student reviews"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                  isAnimationActive={false}
                >
                  {points.map((point) => (
                    <Cell
                      key={point.label}
                      fill={
                        point.isInScoreWindow
                          ? STUDENT_COLOR
                          : STUDENT_COLOR_OUTSIDE_SCORE_WINDOW
                      }
                    />
                  ))}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <p className={"text-xs text-slate-400"}>
            The selection algorithm only uses student reviews from the{" "}
            {scoreWindowLabel}, and more recent ones weigh more (a review loses
            half of its weight every {STUDENT_REVIEW_DECAY_HALF_LIFE_DAYS}{" "}
            days). Monthly averages here are plain averages and are shown for
            trend only. The coordinator score always comes from the latest
            coordinator review, whatever its date: coordinators review each
            teacher once per review period, so the orange line holds each review
            until the next one.
          </p>
        </div>
      )}
    </Card>
  );
}
